/* Product grouping for Rebuy widgets — valkathome-nl
 * ---------------------------------------------------------------------------
 * Surfaces colour siblings as swatches on a Rebuy product card, so "Kussensloop
 * White / Greige / Anthracite" reads as one product in three colours instead of
 * three separate recommendations.
 *
 * HOW THIS THEME DIFFERS FROM THE STANDARD (Oofos) PATTERN — read before editing:
 *
 *   Oofos: every grouped product carries Colour as option2, so stitching merges
 *          sibling variants into an option that already exists.
 *   Here:  there is NO colour option. Colour IS the product — each colourway is a
 *          separate Shopify product, related by the `linked.products` product-list
 *          metafield, with its own colour in the `linked.color` metafield.
 *
 * So this script SYNTHESISES a Colour option rather than merging one, and a swatch
 * click switches to the sibling product's variant (preserving the chosen size where
 * that sibling stocks it) rather than just flipping an option value.
 *
 * Mirrors the theme's own fallback chain (snippets/product-card.liquid:262-303):
 *   linked.color hex  ->  sibling featured image  ->  omit the swatch
 *
 * FAILS SAFE: any missing data, unexpected payload shape, or failed request leaves
 * the widget exactly as it was. It never throws into Rebuy's render loop.
 *
 * Set window.REBUY_GROUPING_DEBUG = true (or add ?rebuy_grouping_debug to the URL)
 * to print what Rebuy actually returned — see readMetafields() for why that matters.
 */
(function () {
  'use strict';

  var NS = 'linked';
  var KEY_PRODUCTS = 'products';
  var KEY_COLOR = 'color';
  var KEY_TITLE = 'title';

  var DEBUG =
    window.REBUY_GROUPING_DEBUG === true ||
    /[?&]rebuy_grouping_debug/.test(window.location.search);

  function log() {
    if (!DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    console.log.apply(console, ['[rebuy-grouping]'].concat(args));
  }

  // Rebuy has shipped metafields in more than one shape over the years, and this
  // theme's passthrough is dashboard-configured, so normalise rather than assume:
  //   array  -> [{ namespace, key, value }, ...]
  //   object -> { linked: { products: '...', color: '...' } }
  // Returns {} when metafields are absent entirely, which is the signal that the
  // Rebuy dashboard has not been set to pass the `linked` namespace through.
  function readMetafields(product) {
    var raw = product && product.metafields;
    if (!raw) return {};
    if (Array.isArray(raw)) {
      return raw.reduce(function (acc, mf) {
        if (!mf || !mf.namespace) return acc;
        acc[mf.namespace] = acc[mf.namespace] || {};
        acc[mf.namespace][mf.key] = mf.value;
        return acc;
      }, {});
    }
    if (typeof raw === 'object') return raw;
    return {};
  }

  // A product-list metafield can arrive as a JSON array of GIDs, a single GID, or a
  // comma-separated string. Normalise all three to numeric Shopify product ids.
  function parseProductReferences(value) {
    if (!value) return [];
    var list = [];
    if (Array.isArray(value)) {
      list = value;
    } else if (typeof value === 'string') {
      var trimmed = value.trim();
      if (trimmed.charAt(0) === '[') {
        try {
          list = JSON.parse(trimmed);
        } catch (e) {
          log('could not JSON.parse product list metafield:', trimmed);
          return [];
        }
      } else {
        list = trimmed.split(',');
      }
    } else {
      return [];
    }
    return list
      .map(function (entry) {
        if (entry == null) return null;
        var str = typeof entry === 'object' ? entry.id || entry.gid || '' : String(entry);
        var match = String(str).match(/(\d+)\s*$/);
        return match ? match[1] : null;
      })
      .filter(Boolean);
  }

  function productIdOf(product) {
    if (!product) return null;
    var id = product.id != null ? product.id : product.product_id;
    var match = String(id == null ? '' : id).match(/(\d+)\s*$/);
    return match ? match[1] : null;
  }

  // The swatch's visible name. The theme prefers a `linked.title` override over the
  // product title (product-linked-products.liquid), so match that precedence.
  function colourNameOf(product, metafields) {
    var linked = metafields[NS] || {};
    if (linked[KEY_TITLE]) return String(linked[KEY_TITLE]).trim();
    return String(product.title || '').trim();
  }

  // Resolution order is deliberate — cheapest first:
  //   1. siblings already in this widget's payload (no request at all)
  //   2. Rebuy's static products API, batched across the whole widget
  // `metafields=yes` is the load-bearing param: without it siblings come back with
  // no `linked.color` and every swatch would fall through to its product image.
  var resolvedCache = Object.create(null);

  function siblingsFromPayload(ids, payloadProducts) {
    var found = Object.create(null);
    (payloadProducts || []).forEach(function (candidate) {
      var cid = productIdOf(candidate);
      if (cid && ids.indexOf(cid) !== -1) found[cid] = candidate;
    });
    return found;
  }

  function rebuyApiKey() {
    return (window.Rebuy && window.Rebuy.shop && window.Rebuy.shop.api_key) || null;
  }

  // One batched request for every unresolved sibling across the widget. Resolves to a
  // map of id -> raw Rebuy product. Never rejects: on any failure the caller simply
  // sees fewer siblings and skips grouping for those cards.
  function fetchSiblings(ids) {
    var missing = ids.filter(function (id) {
      return !(id in resolvedCache);
    });
    if (!missing.length) return Promise.resolve(resolvedCache);

    var key = rebuyApiKey();
    if (!key) {
      log('no Rebuy API key on window.Rebuy.shop.api_key — cannot resolve siblings');
      missing.forEach(function (id) {
        resolvedCache[id] = null;
      });
      return Promise.resolve(resolvedCache);
    }

    var params = new URLSearchParams({
      key: key,
      metafields: 'yes',
      shopify_product_ids: missing.join(','),
      limit: '100',
    });

    log('fetching', missing.length, 'siblings from Rebuy');
    return fetch('https://rebuyengine.com/api/v1/products/static?' + params.toString(), {
      method: 'GET',
      headers: { 'Content-type': 'application/json; charset=UTF-8' },
    })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (body) {
        var data = (body && body.data) || [];
        data.forEach(function (p) {
          var pid = productIdOf(p);
          if (pid) resolvedCache[pid] = p;
        });
        // Mark anything the API didn't return so we never re-request it
        missing.forEach(function (id) {
          if (!(id in resolvedCache)) resolvedCache[id] = null;
        });
        log('resolved', data.length, 'of', missing.length);
        return resolvedCache;
      })
      .catch(function (err) {
        log('sibling fetch failed', err);
        missing.forEach(function (id) {
          resolvedCache[id] = null;
        });
        return resolvedCache;
      });
  }

  // Builds the synthetic Colour option + swatch colour map and merges sibling
  // variants in, so Rebuy's own selectVariantByOption() can resolve a swatch click.
  function applyGrouping(product, siblings) {
    // `linked.color` is REQUIRED, not "colour or image". Verified live: the merchant also
    // uses linked.products for plain related products — Hotel Hoofdkussen links to
    // "Hotel Hoofdkussen XL" — and those siblings have no linked.color. Accepting an image
    // fallback turned them into a bogus Colour option with values like "Hotel Hoofdkussen XL".
    // A real colourway always carries a colour, so that flag is the discriminator.
    var entries = siblings.filter(function (s) {
      return s && s.name && s.color;
    });
    if (entries.length < 2) {
      product.productGrouping = [];
      return false;
    }

    product.swatchColors = {};
    entries.forEach(function (entry) {
      product.swatchColors[entry.name] = entry.color
        ? { value: entry.color, image: false }
        : { value: entry.image, image: true };
    });

    // Merge sibling variants, SHIFTING the colour into option1.
    //
    // This shift is the load-bearing part, verified live: every Rebuy helper the swatch
    // markup depends on — filterOOSOptions(), displayColorSwatches(), isDisabledOptionValue(),
    // selectVariantByOption() — resolves an option's values against the variants' option1/2/3
    // fields. The sibling products have no colour option of their own (colour IS the product
    // here), so their option1 is the size. Without the shift the synthesised Colour option has
    // values that exist on no variant, filterOOSOptions() returns an empty list, and the
    // colour row renders as an EMPTY div — which is exactly what it did.
    //
    // variant.id is untouched, so add-to-cart still resolves the correct variant; only the
    // display/selection fields move.
    // Rebuild product.images from EVERY grouped product, same as the Oofos stitch. itemImage()
    // looks the selected variant's image up in this array by image_id, so unless every
    // sibling's images are present the swap has nothing to find.
    var mergedImages = [];
    entries.forEach(function (entry) {
      (entry.images || []).forEach(function (image) {
        mergedImages.push(
          Object.assign({}, image, {
            position: mergedImages.length,
            groupProductId: entry.productId,
          })
        );
      });
    });
    if (mergedImages.length) product.images = mergedImages;

    var merged = [];
    entries.forEach(function (entry) {
      (entry.variants || []).forEach(function (variant) {
        merged.push(
          Object.assign({}, variant, {
            // NO option1/2/3 shift. It was tried and reverted: moving size out of option1 so
            // the colour could occupy it broke isDisabledOptionValue() for the NATIVE options
            // too, and every size rendered struck-through as out of stock. Rebuy's helpers are
            // left to match the real, unmodified option fields; the synthesised colour option
            // is driven entirely by our own values/handlers instead (see the template's
            // isGroupColour branches).
            // Rebuy's static products API returns inventory_quantity/inventory_policy but NOT
            // an `available` boolean, and its OOS helpers gate on exactly that.
            available:
              typeof variant.available === 'boolean'
                ? variant.available
                : variant.inventory_quantity > 0 || variant.inventory_policy === 'continue',
            groupColour: entry.name,
            groupProductId: entry.productId,
            groupProductHandle: entry.handle,
            // image_id is the join key itemImage() uses against product.images above;
            // image_url is the direct fallback. Oofos sets both — only setting the URL is
            // why selecting a colour left the photo unchanged.
            image_id: variant.image_id || entry.imageId,
            image_url: entry.image || variant.image_url,
          })
        );
      });
    });
    if (merged.length) {
      product.variants = merged;

      // Re-point selected_variant at its CLONE in the merged array, and reseed the product-level
      // option state from it. Both matter:
      //   1. merged holds Object.assign() copies, so the pre-existing selected_variant object is
      //      no longer a member of product.variants and carries no groupProductId. Every grouping
      //      helper keys off that field, so on first interaction rebuyGroupingSelectOption() found
      //      `String(undefined)` siblings, bailed to null, and the template's `|| selectVariantByOption`
      //      fallback then matched the STALE product.option1..3 against the whole merged array —
      //      whose first hit belongs to colour #1. That is why the very first size click jumped
      //      the card back to the first colour.
      //   2. rebuyGroupingColourSelected() reads selected_variant.groupProductId, so with the stale
      //      object no swatch rendered as selected on initial paint.
      var current =
        merged.filter(function (v) {
          return v.id === product.selected_variant_id;
        })[0] ||
        merged.filter(function (v) {
          return v.available !== false;
        })[0] ||
        merged[0];
      var owner = entries.filter(function (e) {
        return String(e.productId) === String(current && current.groupProductId);
      })[0];
      syncSelection(product, current, owner ? owner.name : null);
    }

    // ==========================================================================
    // product.options is left ENTIRELY ALONE. This is the important part.
    //
    // Earlier versions synthesised a "Color" option and pushed it into
    // product.options. That single decision caused every out-of-stock bug seen in
    // this feature: Rebuy's isDisabledOptionValue() evaluates an option value
    // against the full combination of options, so a colour value that exists on no
    // variant made EVERY size resolve as unavailable — struck through in the cart
    // widget and in the dynamic bundle alike. Trying to satisfy it the other way
    // (shifting colour into variant.option1) broke the native fields instead.
    //
    // The colour row is therefore rendered straight from product.productGrouping in
    // the templates, with its own handlers, and Rebuy keeps sole ownership of the
    // real options. Do not reintroduce a synthesised option here.
    // ==========================================================================
    product.productGrouping = entries;
    return true;
  }

  function collectSibling(raw, selfId) {
    if (!raw) return null;
    var mf = readMetafields(raw);
    var linked = mf[NS] || {};
    return {
      productId: productIdOf(raw) || selfId,
      handle: raw.handle,
      name: colourNameOf(raw, mf),
      // `name` is the SWATCH label (linked.title override, else the title). `title` is the
      // sibling's real product title, used for the card heading when its colour is picked.
      title: String(raw.title || '').trim(),
      handleUrl: raw.handle ? '/products/' + raw.handle : null,
      color: linked[KEY_COLOR] || null,
      image:
        (raw.featured_image && (raw.featured_image.src || raw.featured_image)) ||
        (raw.image && raw.image.src) ||
        (raw.images && raw.images[0] && (raw.images[0].src || raw.images[0])) ||
        null,
      // imageId + the full images array are what make the image SWAP work, per the Oofos
      // stitch: itemImage(product, variant) resolves a variant's picture by matching
      // variant.image_id against an entry in product.images. Carrying only a URL (as this
      // did) leaves itemImage() with nothing to match, so the card kept the seed's photo.
      imageId: (raw.image && raw.image.id) || (raw.images && raw.images[0] && raw.images[0].id) || null,
      images: raw.images || [],
      variants: raw.variants || [],
    };
  }

  function stitchProduct(product, payloadProducts) {
    var mf = readMetafields(product);
    var linked = mf[NS] || {};
    var refIds = parseProductReferences(linked[KEY_PRODUCTS]);

    log('product', product.title, {
      hasMetafields: Object.keys(mf).length > 0,
      linkedNamespacePresent: !!mf[NS],
      rawProductsMetafield: linked[KEY_PRODUCTS],
      parsedSiblingIds: refIds,
      ownColour: linked[KEY_COLOR],
    });

    if (!refIds.length) {
      product.productGrouping = [];
      return false;
    }

    var self = collectSibling(product, productIdOf(product));
    var inPayload = siblingsFromPayload(refIds, payloadProducts);
    var resolved = refIds.map(function (id) {
      var raw = inPayload[id] || resolvedCache[id];
      return raw ? collectSibling(raw, id) : null;
    });

    var all = [self].concat(resolved).filter(Boolean);
    var seen = Object.create(null);
    var unique = all.filter(function (entry) {
      if (!entry.productId || seen[entry.productId]) return false;
      seen[entry.productId] = true;
      return true;
    });
    var applied = applyGrouping(product, unique);
    log('stitched', product.title, {
      referenced: refIds.length,
      siblings: unique.length,
      applied: applied,
    });
    return applied;
  }

  // Every id referenced anywhere in this widget, so the fetch is one request
  // rather than one per card.
  function collectAllReferencedIds(products) {
    var ids = Object.create(null);
    products.forEach(function (product) {
      var linked = readMetafields(product)[NS] || {};
      parseProductReferences(linked[KEY_PRODUCTS]).forEach(function (id) {
        ids[id] = true;
      });
    });
    return Object.keys(ids);
  }

  // The ONLY place selection state is written. Mirrors Rebuy's own contract, which is wider
  // than `selected_variant`: selecting a variant also copies its option1/2/3 up onto the
  // PRODUCT, and every swatch helper reads that product-level copy, not the variant —
  //   hasSwatchOptionSelected()  -> product['option' + position] === value   (the :checked binding)
  //   isDisabledOptionValue()    -> simulates product.option{n}, then matches the variants
  //   the variant match          -> variant.option1/2/3 === product.option1/2/3
  // so setting selected_variant alone left product.option1..3 on the seed variant's values: the
  // clicked swatch never showed as active (Vue re-rendered :checked back to the stale value) and
  // availability was computed for the wrong variant. The theme's own FormWatcher.selectVariant()
  // in rebuy-extensions.liquid already does this — the grouping helpers were the outlier.
  function syncSelection(product, variant, colourName) {
    if (!product || !variant) return null;
    product.selected_variant = variant;
    product.selected_variant_id = variant.id;
    for (var n = 1; n <= 3; n++) {
      product['option' + n] = variant['option' + n] != null ? variant['option' + n] : null;
    }
    if (colourName != null) product.selectedGroupColour = colourName;
    return variant;
  }

  // Swatch style for one colour value, read off the map applyGrouping() built.
  // The templates call THIS, not Rebuy's getMerchantThemeColorAssetStyle(), which reads a
  // window.theme.colors global this theme does not define — that call returning nothing is why
  // the swatches rendered with no background at all.
  window.rebuyGroupingSwatchStyle = function (product, value) {
    var entry = product && product.swatchColors && product.swatchColors[value];
    if (!entry) return {};
    return entry.image
      ? { backgroundImage: 'url(' + entry.value + ')' }
      : { backgroundColor: entry.value };
  };

  // Colour swatch click. Rebuy's selectVariantByOption() can't serve this: the colour is not a
  // real option on any variant, it identifies a sibling PRODUCT. So switch to that sibling's
  // variant directly, preserving the shopper's current size where the sibling stocks it.
  window.rebuyGroupingSelectColour = function (product, value) {
    var entry = (product.productGrouping || []).filter(function (g) {
      return g && g.name === value;
    })[0];
    if (!entry) return;
    var current = product.selected_variant || {};
    var candidates = (product.variants || []).filter(function (v) {
      return String(v.groupProductId) === String(entry.productId);
    });
    // Carry EVERY option across, not just option1: which field holds the size varies by
    // product now that product.options is left native, so hardcoding option1 would drop the
    // shopper's choice on any product whose first option isn't the size.
    var sameOptions = candidates.filter(function (v) {
      for (var n = 1; n <= 3; n++) {
        var k = 'option' + n;
        if (current[k] != null && v[k] !== current[k]) return false;
      }
      return true;
    });
    var match =
      sameOptions.filter(function (v) { return v.available !== false; })[0] ||
      sameOptions[0] ||
      candidates.filter(function (v) { return v.available !== false; })[0] ||
      candidates[0];
    if (!match) return null;
    return syncSelection(product, match, value);
  };

  // Colour-aware option selection. Rebuy's selectVariantByOption() scans product.variants for
  // the FIRST variant carrying the chosen value — and because the array holds every sibling's
  // variants in order, that first match belongs to the first colour. Result: picking a size
  // after choosing a colour silently reset the card to colour #1.
  //
  // This restricts the search to variants belonging to the currently selected sibling, and
  // preserves the shopper's other option values where a variant exists for that combination.
  //
  // Return contract, which the template's `... || selectVariantByOption(...)` chain depends on:
  //   NOT grouped, or the option isn't ours  -> null, so Rebuy's native handler takes the click.
  //   grouped, matched                       -> the variant.
  //   grouped, NO match in the current colour-> the CURRENT variant (truthy, no-op). Returning
  //     null here was wrong: it handed the click to selectVariantByOption(), which matches across
  //     the whole merged array and so silently jumped the card to whichever colour happens to
  //     stock that size. A grouped product must never let the native handler cross a colour.
  // Resolves an option NAME to its variant field key. Shared by the selection and disabled-state
  // helpers so both agree on which field an option lives in; product.options is native here, so
  // the position is whatever Shopify assigned and cannot be hardcoded.
  function optionKeyFor(product, optionName) {
    var options = (product && product.options) || [];
    for (var i = 0; i < options.length; i++) {
      if (options[i] && options[i].name === optionName) return 'option' + (i + 1);
    }
    return null;
  }

  // Variants belonging to the sibling product that currently owns the selection.
  function currentColourVariants(product) {
    var current = (product && product.selected_variant) || {};
    return (product.variants || []).filter(function (v) {
      return String(v.groupProductId) === String(current.groupProductId);
    });
  }

  // Whether an option value is unavailable IN THE CURRENTLY SELECTED COLOUR. Rebuy's own
  // isDisabledOptionValue() cannot answer this: it simulates the value onto product.option{n} and
  // matches against product.variants, which after stitching spans every
  // sibling — so a size stocked only in another colour resolved as available and rendered enabled.
  // Clicking it then did nothing (rebuyGroupingSelectOption() no-ops rather than cross colours),
  // which reads as a dead swatch. Chained ahead of Rebuy's helper in the templates, so `false`
  // here still defers to the native availability check.
  window.rebuyGroupingOptionDisabled = function (product, optionName, value) {
    if (!product || !product.productGrouping || product.productGrouping.length < 2) return false;
    var key = optionKeyFor(product, optionName);
    if (!key) return false;
    return !currentColourVariants(product).some(function (v) {
      return v[key] === value && v.available !== false;
    });
  };

  window.rebuyGroupingSelectOption = function (product, optionName, value) {
    if (!product || !product.productGrouping || product.productGrouping.length < 2) return null;
    var key = optionKeyFor(product, optionName);
    if (!key) return null;

    var current = product.selected_variant || {};
    var sameColour = currentColourVariants(product);
    if (!sameColour.length) return current.id ? current : null;

    var candidates = sameColour.filter(function (v) {
      return v[key] === value;
    });
    if (!candidates.length) return current.id ? current : null;

    // Prefer the candidate that also keeps every other option as the shopper had it
    var exact = candidates.filter(function (v) {
      for (var n = 1; n <= 3; n++) {
        var k = 'option' + n;
        if (k === key) continue;
        if (current[k] != null && v[k] !== current[k]) return false;
      }
      return true;
    });
    var match =
      exact.filter(function (v) { return v.available !== false; })[0] ||
      exact[0] ||
      candidates.filter(function (v) { return v.available !== false; })[0] ||
      candidates[0];

    // Pass the owning colour through so the colour row stays in step with the size row. Leaving
    // selectedGroupColour untouched here let the two rows drift: rebuyGroupingColourSelected()
    // prefers selectedGroupColour, so it kept reporting the previously clicked colour.
    var owner = product.productGrouping.filter(function (g) {
      return g && String(g.productId) === String(match.groupProductId);
    })[0];
    return syncSelection(product, match, owner ? owner.name : null);
  };

  // Which colour is currently active — the sibling that owns the selected variant.
  window.rebuyGroupingColourSelected = function (product, value) {
    if (product && product.selectedGroupColour) return product.selectedGroupColour === value;
    var v = product && product.selected_variant;
    if (!v) return false;
    var entry = (product.productGrouping || []).filter(function (g) {
      return g && String(g.productId) === String(v.groupProductId);
    })[0];
    return !!entry && entry.name === value;
  };

  // Card heading for the currently selected colour. The seed product's title is only correct
  // until a swatch is clicked — after that the card is showing a different product, so it read
  // "Kussensloop - White" while displaying the Anthracite photo and variant.
  window.rebuyGroupingTitle = function (product) {
    var v = product && product.selected_variant;
    if (!v || !product.productGrouping) return product ? product.title : '';
    var entry = product.productGrouping.filter(function (g) {
      return g && String(g.productId) === String(v.groupProductId);
    })[0];
    return (entry && entry.title) || product.title;
  };

  document.addEventListener('rebuy.productsChange', function (event) {
    var widget = event && event.detail && event.detail.widget;
    var products = widget && widget.data && widget.data.products;
    if (!products || !products.length) return;

    // Resolve every referenced sibling first, then stitch synchronously. Doing the
    // fetch up front keeps it to a single request and means each card is stitched in
    // one pass instead of re-rendering as individual lookups land.
    var referenced = collectAllReferencedIds(products);
    if (!referenced.length) return;

    fetchSiblings(referenced).then(function () {
      var applied = products.map(function (product) {
        try {
          return stitchProduct(product, products);
        } catch (err) {
          console.error('[rebuy-grouping] stitch failed for', product && product.title, err);
          product.productGrouping = [];
          return false;
        }
      });

      if (
        applied.some(Boolean) &&
        widget.View &&
        widget.View.vue &&
        typeof widget.View.vue.$forceUpdate === 'function'
      ) {
        widget.View.vue.$forceUpdate();
      }
    });
  });

  log('initialised. Set window.REBUY_GROUPING_DEBUG = true for per-product output.');
})();
